import React, { useState, useEffect } from 'react';
import cobranzasService from '../../services/cobranzasService';

const PanelCobranzas = () => {
    const [activeTab, setActiveTab] = useState('pending'); // 'pending', 'config', 'history', 'accounts'
    const [stats, setStats] = useState(null);
    const [receipts, setReceipts] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [systemStatus, setSystemStatus] = useState(null);
    const [config, setConfig] = useState({
        default_due_day: 5,
        default_cutoff_day: 6,
        default_monthly_amount: '20.00',
        title: 'Aviso de Pago de Suscripción Mensual',
        instructions: '',
        payment_image_url: ''
    });

    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('ALL');

    // Modales y estados de acción
    const [selectedImage, setSelectedImage] = useState(null);
    const [rejectingReceipt, setRejectingReceipt] = useState(null);
    const [rejectionReason, setRejectionReason] = useState('');
    const [actionLoading, setActionLoading] = useState(false);
    const [editingAccount, setEditingAccount] = useState(null);
    const [notification, setNotification] = useState(null);

    // Modal de Corte Manual de Servicio
    const [showCutServiceModal, setShowCutServiceModal] = useState(false);
    const [cutServiceReason, setCutServiceReason] = useState('Servicio suspendido por falta de pago de suscripción.');

    // Archivo de imagen de pago para subir
    const [bannerFile, setBannerFile] = useState(null);
    const [bannerPreview, setBannerPreview] = useState('');

    const loadData = async () => {
        try {
            setLoading(true);
            const [statsRes, pendingReceiptsRes, accountsRes, configRes, myStatusRes] = await Promise.all([
                cobranzasService.getStats(),
                cobranzasService.getReceipts({ status: activeTab === 'pending' ? 'PENDING' : statusFilter }),
                cobranzasService.getAccounts(),
                cobranzasService.getConfig(),
                cobranzasService.getMyStatus(),
            ]);
            setStats(statsRes);
            setReceipts(pendingReceiptsRes);
            setAccounts(accountsRes);
            setSystemStatus(myStatusRes);
            if (configRes) {
                setConfig(configRes);
                if (configRes.payment_image_url) {
                    setBannerPreview(cobranzasService.getMediaUrl(configRes.payment_image_url));
                }
            }
        } catch (error) {
            console.error('Error cargando datos de cobranzas:', error);
            showNotification('Error cargando información de cobranzas', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, [activeTab, statusFilter]);

    const showNotification = (msg, type = 'success') => {
        setNotification({ msg, type });
        setTimeout(() => setNotification(null), 4000);
    };

    // Handler para el Interruptor Maestro de Bloqueo / Desbloqueo Manual
    const handleToggleManualBlock = async (shouldBlock, reason = '') => {
        try {
            setActionLoading(true);
            await cobranzasService.toggleBlock(shouldBlock, reason);
            setShowCutServiceModal(false);
            showNotification(
                shouldBlock
                    ? 'Servicio cortado y bloqueado manualmente para todos los usuarios.'
                    : 'Servicio reactivado y desbloqueado exitosamente para todo el sistema.'
            );
            await loadData();
        } catch (error) {
            console.error('Error alternando corte de servicio:', error);
            showNotification(error.response?.data?.detail || 'Error al modificar estado del servicio', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    const handleApprove = async (receiptId) => {
        if (!window.confirm('¿Está seguro de APROBAR este comprobante de pago? La cuenta será activada formalmente.')) {
            return;
        }

        try {
            setActionLoading(true);
            await cobranzasService.reviewReceipt(receiptId, 'approve');
            showNotification('Comprobante aprobado y cuenta activada exitosamente.');
            loadData();
        } catch (error) {
            console.error('Error aprobando comprobante:', error);
            showNotification(error.response?.data?.detail || 'Error al aprobar comprobante', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    const handleRejectSubmit = async (e) => {
        e.preventDefault();
        if (!rejectionReason.trim()) {
            alert('Por favor ingrese el motivo del rechazo.');
            return;
        }

        try {
            setActionLoading(true);
            await cobranzasService.reviewReceipt(rejectingReceipt.id, 'reject', rejectionReason);
            showNotification('Comprobante rechazado. La cuenta ha sido bloqueada automáticamente.', 'info');
            setRejectingReceipt(null);
            setRejectionReason('');
            loadData();
        } catch (error) {
            console.error('Error rechazando comprobante:', error);
            showNotification(error.response?.data?.detail || 'Error al rechazar comprobante', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    const handleSaveAccount = async (e) => {
        e.preventDefault();
        try {
            setActionLoading(true);
            await cobranzasService.updateAccount(editingAccount.id, {
                monthly_amount: editingAccount.monthly_amount,
                due_day: editingAccount.due_day,
                cutoff_day: editingAccount.cutoff_day,
                account_name: editingAccount.account_name,
                notes: editingAccount.notes,
                status: editingAccount.status,
            });
            showNotification('Configuración de cuenta actualizada.');
            setEditingAccount(null);
            loadData();
        } catch (error) {
            console.error('Error actualizando cuenta:', error);
            showNotification('Error al actualizar cuenta', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    const handleBannerFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setBannerFile(file);
            setBannerPreview(URL.createObjectURL(file));
        }
    };

    const handleSaveGlobalConfig = async (e) => {
        e.preventDefault();
        try {
            setActionLoading(true);
            const formData = new FormData();
            formData.append('default_due_day', config.default_due_day);
            formData.append('default_cutoff_day', config.default_cutoff_day);
            formData.append('default_monthly_amount', config.default_monthly_amount);
            formData.append('title', config.title || '');

            if (bannerFile) {
                formData.append('payment_image', bannerFile);
            }

            const updated = await cobranzasService.updateConfig(formData);
            if (updated?.config) {
                setConfig(updated.config);
                if (updated.config.payment_image_url) {
                    setBannerPreview(cobranzasService.getMediaUrl(updated.config.payment_image_url));
                }
            }
            showNotification('¡Configuración de fechas e imagen de pago guardadas exitosamente!');
        } catch (error) {
            console.error('Error guardando configuración:', error);
            const errorMsg = error.response?.data?.detail || (typeof error.response?.data === 'object' ? Object.values(error.response.data).flat().join(', ') : '') || 'Error al guardar la configuración';
            showNotification(errorMsg, 'error');
        } finally {
            setActionLoading(false);
        }
    };

    const isSystemBlocked = Boolean(systemStatus?.is_blocked || systemStatus?.is_manually_blocked);

    return (
        <div style={{ padding: '2rem', maxWidth: '1400px', margin: '0 auto' }}>
            {/* Notificación Toast */}
            {notification && (
                <div style={{
                    position: 'fixed',
                    top: '20px',
                    right: '20px',
                    zIndex: 3000,
                    padding: '1rem 1.5rem',
                    borderRadius: '8px',
                    background: notification.type === 'error' ? '#ef4444' : (notification.type === 'info' ? '#f59e0b' : '#10b981'),
                    color: '#fff',
                    boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem'
                }}>
                    <i className={`bi ${notification.type === 'error' ? 'bi-x-circle-fill' : 'bi-check-circle-fill'}`}></i>
                    {notification.msg}
                </div>
            )}

            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
                <div>
                    <h1 style={{ fontSize: '1.875rem', fontWeight: 'bold', color: '#1e293b', margin: 0 }}>
                        <i className="bi bi-cash-stack" style={{ color: 'var(--primary-color, #4a1f0c)', marginRight: '0.75rem' }}></i>
                        Gestión de Cobranzas y Suscripciones
                    </h1>
                    <p style={{ color: '#64748b', margin: '0.25rem 0 0 0', fontSize: '0.95rem' }}>
                        Control de corte de servicio, fechas de vencimiento, flyer/imagen de pago y auditoría de comprobantes.
                    </p>
                </div>
                <button
                    onClick={loadData}
                    style={{
                        background: '#f8fafc',
                        border: '1px solid #cbd5e1',
                        padding: '0.6rem 1.2rem',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        color: '#475569',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                    }}
                >
                    <i className="bi bi-arrow-clockwise"></i> Actualizar
                </button>
            </div>

            {/* INTERRUPTOR MAESTRO: Control Manual de Corte y Estado del Servicio */}
            <div style={{
                background: isSystemBlocked
                    ? 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)'
                    : 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                border: `2px solid ${isSystemBlocked ? '#f87171' : '#86efac'}`,
                borderRadius: '16px',
                padding: '1.5rem 1.75rem',
                marginBottom: '2rem',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '1.25rem',
                boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem' }}>
                    <div style={{
                        width: '56px',
                        height: '56px',
                        borderRadius: '14px',
                        background: isSystemBlocked ? '#ef4444' : '#16a34a',
                        color: '#ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.8rem',
                        boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
                        flexShrink: 0
                    }}>
                        <i className={isSystemBlocked ? 'bi bi-shield-x' : 'bi bi-shield-check'}></i>
                    </div>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                            <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 'bold', color: isSystemBlocked ? '#991b1b' : '#14532d' }}>
                                {isSystemBlocked
                                    ? 'SERVICIO CORTADO / BLOQUEADO (ACCESO SUSPENDIDO)'
                                    : 'SERVICIO ACTIVO (ACCESO HABILITADO)'}
                            </h3>
                            <span style={{
                                padding: '0.2rem 0.75rem',
                                borderRadius: '20px',
                                fontSize: '0.75rem',
                                fontWeight: 'bold',
                                background: isSystemBlocked ? '#dc2626' : '#16a34a',
                                color: '#ffffff'
                            }}>
                                {systemStatus?.status || 'ESTADO'}
                            </span>
                        </div>
                        <p style={{ margin: '0.35rem 0 0 0', fontSize: '0.875rem', color: isSystemBlocked ? '#7f1d1d' : '#166534', maxWidth: '750px' }}>
                            {isSystemBlocked
                                ? `El sistema se encuentra bloqueado para todos los usuarios. Al intentar entrar al POS o dashboard verán la pantalla de pago con tu imagen y el formulario para subir el comprobante. ${systemStatus?.manual_block_reason ? `[Motivo: ${systemStatus.manual_block_reason}]` : ''}`
                                : 'Todos los usuarios y puntos de venta tienen acceso normal. El ciclo automático alertará del día 1 al 5 y cortará el día 6 si no se reporta el pago.'}
                        </p>
                    </div>
                </div>

                {/* Botón de Acción de Corte / Reactivación */}
                <div>
                    {isSystemBlocked ? (
                        <button
                            onClick={() => handleToggleManualBlock(false)}
                            disabled={actionLoading}
                            style={{
                                background: '#16a34a',
                                color: '#ffffff',
                                border: 'none',
                                padding: '0.85rem 1.75rem',
                                borderRadius: '10px',
                                fontWeight: 'bold',
                                fontSize: '0.95rem',
                                cursor: actionLoading ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.6rem',
                                boxShadow: '0 4px 6px -1px rgba(22, 163, 74, 0.3)',
                                transition: 'all 0.2s ease'
                            }}
                        >
                            <i className="bi bi-unlock-fill"></i>
                            {actionLoading ? 'Reactivando...' : 'Reactivar / Desbloquear Servicio'}
                        </button>
                    ) : (
                        <button
                            onClick={() => setShowCutServiceModal(true)}
                            disabled={actionLoading}
                            style={{
                                background: '#dc2626',
                                color: '#ffffff',
                                border: 'none',
                                padding: '0.85rem 1.75rem',
                                borderRadius: '10px',
                                fontWeight: 'bold',
                                fontSize: '0.95rem',
                                cursor: actionLoading ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.6rem',
                                boxShadow: '0 4px 6px -1px rgba(220, 38, 38, 0.3)',
                                transition: 'all 0.2s ease'
                            }}
                        >
                            <i className="bi bi-slash-circle-fill"></i>
                            Cortar Servicio Ahora (Bloquear Sistema)
                        </button>
                    )}
                </div>
            </div>

            {/* Estadísticas / KPIs */}
            {stats && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
                    <div style={{ background: '#ffffff', padding: '1.25rem', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                        <div style={{ color: '#64748b', fontSize: '0.85rem', fontWeight: '600' }}>TOTAL USUARIOS / CUENTAS</div>
                        <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#1e293b', marginTop: '0.25rem' }}>{stats.total_accounts}</div>
                    </div>
                    <div style={{ background: '#f0fdf4', padding: '1.25rem', borderRadius: '12px', border: '1px solid #bbf7d0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                        <div style={{ color: '#166534', fontSize: '0.85rem', fontWeight: '600' }}>ESTADO DEL SISTEMA</div>
                        <div style={{ fontSize: '1.35rem', fontWeight: 'bold', color: '#15803d', marginTop: '0.5rem' }}>
                            {isSystemBlocked ? 'SUSPENDIDO' : 'ACTIVO / AL DÍA'}
                        </div>
                    </div>
                    <div style={{ background: '#fefce8', padding: '1.25rem', borderRadius: '12px', border: '1px solid #fef08a', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                        <div style={{ color: '#854d0e', fontSize: '0.85rem', fontWeight: '600' }}>PERÍODO DE AVISO (DÍAS 1 AL {config.default_due_day})</div>
                        <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#a16207', marginTop: '0.25rem' }}>Día {config.default_due_day}</div>
                    </div>
                    <div style={{ background: '#eff6ff', padding: '1.25rem', borderRadius: '12px', border: '1px solid #bfdbfe', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                        <div style={{ color: '#1e40af', fontSize: '0.85rem', fontWeight: '600' }}>POR AUDITAR (EN REVISIÓN)</div>
                        <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#2563eb', marginTop: '0.25rem' }}>{stats.pending_receipts_count}</div>
                    </div>
                    <div style={{ background: '#fef2f2', padding: '1.25rem', borderRadius: '12px', border: '1px solid #fecaca', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                        <div style={{ color: '#991b1b', fontSize: '0.85rem', fontWeight: '600' }}>DÍA DE CORTE AUTOMÁTICO</div>
                        <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#dc2626', marginTop: '0.25rem' }}>Día {config.default_cutoff_day}</div>
                    </div>
                </div>
            )}

            {/* Pestañas de Navegación */}
            <div style={{ display: 'flex', borderBottom: '2px solid #e2e8f0', marginBottom: '1.5rem', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button
                    onClick={() => setActiveTab('pending')}
                    style={{
                        padding: '0.75rem 1.25rem',
                        background: 'transparent',
                        border: 'none',
                        borderBottom: activeTab === 'pending' ? '3px solid var(--primary-color, #4a1f0c)' : '3px solid transparent',
                        color: activeTab === 'pending' ? 'var(--primary-color, #4a1f0c)' : '#64748b',
                        fontWeight: '600',
                        cursor: 'pointer',
                        fontSize: '0.95rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                    }}
                >
                    <i className="bi bi-inbox-fill"></i> Comprobantes por Auditar
                    {stats?.pending_receipts_count > 0 && (
                        <span style={{
                            background: '#ef4444',
                            color: '#fff',
                            borderRadius: '12px',
                            padding: '0.15rem 0.6rem',
                            fontSize: '0.75rem'
                        }}>
                            {stats.pending_receipts_count}
                        </span>
                    )}
                </button>

                <button
                    onClick={() => setActiveTab('config')}
                    style={{
                        padding: '0.75rem 1.25rem',
                        background: 'transparent',
                        border: 'none',
                        borderBottom: activeTab === 'config' ? '3px solid var(--primary-color, #4a1f0c)' : '3px solid transparent',
                        color: activeTab === 'config' ? 'var(--primary-color, #4a1f0c)' : '#64748b',
                        fontWeight: '600',
                        cursor: 'pointer',
                        fontSize: '0.95rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                    }}
                >
                    <i className="bi bi-calendar-event-fill"></i> Configuración de Fechas e Imagen de Pago
                </button>

                <button
                    onClick={() => setActiveTab('history')}
                    style={{
                        padding: '0.75rem 1.25rem',
                        background: 'transparent',
                        border: 'none',
                        borderBottom: activeTab === 'history' ? '3px solid var(--primary-color, #4a1f0c)' : '3px solid transparent',
                        color: activeTab === 'history' ? 'var(--primary-color, #4a1f0c)' : '#64748b',
                        fontWeight: '600',
                        cursor: 'pointer',
                        fontSize: '0.95rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                    }}
                >
                    <i className="bi bi-clock-history"></i> Historial de Comprobantes
                </button>

                <button
                    onClick={() => setActiveTab('accounts')}
                    style={{
                        padding: '0.75rem 1.25rem',
                        background: 'transparent',
                        border: 'none',
                        borderBottom: activeTab === 'accounts' ? '3px solid var(--primary-color, #4a1f0c)' : '3px solid transparent',
                        color: activeTab === 'accounts' ? 'var(--primary-color, #4a1f0c)' : '#64748b',
                        fontWeight: '600',
                        cursor: 'pointer',
                        fontSize: '0.95rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                    }}
                >
                    <i className="bi bi-building"></i> Directorio de Cuentas / Locales
                </button>
            </div>

            {/* TAB: Configuración de Fechas e Imagen de Pago (Simplificado) */}
            {activeTab === 'config' && (
                <form onSubmit={handleSaveGlobalConfig}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>
                        
                        {/* Tarjeta 1: Fechas y Plazos de Cobranza */}
                        <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                            <h3 style={{ fontSize: '1.15rem', fontWeight: 'bold', color: '#1e293b', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <i className="bi bi-calendar3" style={{ color: 'var(--primary-color, #4a1f0c)' }}></i>
                                Parámetros de Fechas y Mensualidad
                            </h3>

                            <div style={{ marginBottom: '1.25rem' }}>
                                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#334155', marginBottom: '0.35rem' }}>
                                    Día Límite de Aviso (Días 1 al X de cada mes):
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    max="31"
                                    value={config.default_due_day}
                                    onChange={(e) => setConfig({ ...config, default_due_day: parseInt(e.target.value) || 5 })}
                                    style={{ width: '100%', padding: '0.65rem 0.85rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.95rem' }}
                                    required
                                />
                                <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                                    Durante los días 1 al {config.default_due_day}, los usuarios verán la alerta amarilla de aviso sin que se les bloquee el sistema.
                                </span>
                            </div>

                            <div style={{ marginBottom: '1.25rem' }}>
                                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#334155', marginBottom: '0.35rem' }}>
                                    Día de Corte / Bloqueo Automático:
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    max="31"
                                    value={config.default_cutoff_day}
                                    onChange={(e) => setConfig({ ...config, default_cutoff_day: parseInt(e.target.value) || 6 })}
                                    style={{ width: '100%', padding: '0.65rem 0.85rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.95rem' }}
                                    required
                                />
                                <span style={{ fontSize: '0.75rem', color: '#dc2626', fontWeight: '500' }}>
                                    A partir del día {config.default_cutoff_day}, el sistema se bloqueará automáticamente a quienes no hayan reportado su pago.
                                </span>
                            </div>

                            <div style={{ marginBottom: '1.25rem' }}>
                                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#334155', marginBottom: '0.35rem' }}>
                                    Monto Mensual por Defecto ($ USD):
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={config.default_monthly_amount}
                                    onChange={(e) => setConfig({ ...config, default_monthly_amount: e.target.value })}
                                    style={{ width: '100%', padding: '0.65rem 0.85rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.95rem' }}
                                    required
                                />
                            </div>

                            <div>
                                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#334155', marginBottom: '0.35rem' }}>
                                    Título de la Alerta:
                                </label>
                                <input
                                    type="text"
                                    value={config.title}
                                    onChange={(e) => setConfig({ ...config, title: e.target.value })}
                                    style={{ width: '100%', padding: '0.65rem 0.85rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.95rem' }}
                                />
                            </div>
                        </div>

                        {/* Tarjeta 2: Subida de Imagen de Pago / Flyer / Banner */}
                        <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                            <h3 style={{ fontSize: '1.15rem', fontWeight: 'bold', color: '#1e293b', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <i className="bi bi-image" style={{ color: 'var(--primary-color, #4a1f0c)' }}></i>
                                Imagen / Flyer de Pago (Cuentas Bancarias y QR)
                            </h3>
                            <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '1.25rem' }}>
                                Sube aquí tu diseño con los números de cuenta, banco, RUC y código QR. Esta imagen se mostrará completa a los usuarios cuando hagan clic en "Ver Cuenta y Pagar" o cuando el sistema esté bloqueado.
                            </p>

                            <div style={{
                                border: '2px dashed #cbd5e1',
                                borderRadius: '12px',
                                padding: '1.5rem',
                                textAlign: 'center',
                                background: '#f8fafc',
                                cursor: 'pointer',
                                marginBottom: '1.25rem',
                                transition: 'all 0.2s ease'
                            }} onClick={() => document.getElementById('flyer-file-input').click()}>
                                <input
                                    id="flyer-file-input"
                                    type="file"
                                    accept="image/*"
                                    onChange={handleBannerFileChange}
                                    style={{ display: 'none' }}
                                />
                                {bannerPreview ? (
                                    <div>
                                        <img
                                            src={bannerPreview}
                                            alt="Flyer de Pago"
                                            style={{
                                                maxHeight: '260px',
                                                maxWidth: '100%',
                                                objectFit: 'contain',
                                                borderRadius: '10px',
                                                boxShadow: '0 4px 6px -1px rgba(0,0,0,0.15)',
                                                border: '1px solid #cbd5e1',
                                                display: 'block',
                                                margin: '0 auto'
                                            }}
                                        />
                                        <p style={{ margin: '0.75rem 0 0 0', fontSize: '0.85rem', color: '#0284c7', fontWeight: 'bold' }}>
                                            <i className="bi bi-arrow-repeat"></i> Clic para cambiar la imagen
                                        </p>
                                    </div>
                                ) : (
                                    <div style={{ padding: '1.5rem 0' }}>
                                        <i className="bi bi-cloud-arrow-up" style={{ fontSize: '3rem', color: 'var(--primary-color, #4a1f0c)' }}></i>
                                        <p style={{ margin: '0.5rem 0 0 0', fontWeight: 'bold', color: '#1e293b', fontSize: '1rem' }}>
                                            Haz clic o arrastra tu imagen / flyer de pago aquí
                                        </p>
                                        <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                                            Formatos recomendados: PNG, JPG, WEBP de alta resolución
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Botón Guardar */}
                    <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
                        <button
                            type="submit"
                            disabled={actionLoading}
                            style={{
                                background: 'var(--primary-color, #4a1f0c)',
                                color: '#ffffff',
                                border: 'none',
                                padding: '0.85rem 2.5rem',
                                borderRadius: '8px',
                                fontWeight: 'bold',
                                fontSize: '1rem',
                                cursor: actionLoading ? 'not-allowed' : 'pointer',
                                boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                            }}
                        >
                            <i className="bi bi-save-fill"></i>
                            {actionLoading ? 'Guardando Configuración...' : 'Guardar Configuración General'}
                        </button>
                    </div>
                </form>
            )}

            {/* TAB 1: Comprobantes por Auditar */}
            {activeTab === 'pending' && (
                <div>
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '3rem' }}>Cargando comprobantes...</div>
                    ) : receipts.length === 0 ? (
                        <div style={{
                            textAlign: 'center',
                            padding: '4rem 2rem',
                            background: '#ffffff',
                            borderRadius: '12px',
                            border: '1px solid #e2e8f0',
                            color: '#64748b'
                        }}>
                            <i className="bi bi-check-circle" style={{ fontSize: '3rem', color: '#10b981', display: 'block', marginBottom: '1rem' }}></i>
                            <h3>¡Todo al día!</h3>
                            <p>No hay comprobantes pendientes de revisión en este momento.</p>
                        </div>
                    ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: '1.5rem' }}>
                            {receipts.map((receipt) => {
                                const ocr = receipt.extracted_data || {};
                                const receiptImgUrl = cobranzasService.getMediaUrl(receipt.image_url);
                                return (
                                    <div
                                        key={receipt.id}
                                        style={{
                                            background: '#ffffff',
                                            borderRadius: '12px',
                                            border: '1px solid #e2e8f0',
                                            overflow: 'hidden',
                                            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
                                            display: 'flex',
                                            flexDirection: 'column'
                                        }}
                                    >
                                        {/* Cabecera Tarjeta */}
                                        <div style={{ padding: '1rem', background: '#f8fafc', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div>
                                                <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: 'bold', color: '#1e293b' }}>
                                                    {receipt.user_details?.first_name ? `${receipt.user_details.first_name} ${receipt.user_details.last_name}` : receipt.user_details?.email}
                                                </h4>
                                                <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                                                    Ciclo: <strong>{receipt.billing_cycle}</strong> • {new Date(receipt.uploaded_at).toLocaleString()}
                                                </span>
                                            </div>
                                            <span style={{
                                                background: '#dbeafe',
                                                color: '#1d4ed8',
                                                padding: '0.2rem 0.6rem',
                                                borderRadius: '6px',
                                                fontSize: '0.75rem',
                                                fontWeight: 'bold'
                                            }}>
                                                PENDIENTE
                                            </span>
                                        </div>

                                        {/* Contenido / OCR Data */}
                                        <div style={{ padding: '1rem', display: 'flex', gap: '1rem', flex: 1 }}>
                                            {/* Miniatura Imagen */}
                                            <div
                                                onClick={() => setSelectedImage(receiptImgUrl)}
                                                style={{
                                                    width: '120px',
                                                    height: '140px',
                                                    borderRadius: '8px',
                                                    overflow: 'hidden',
                                                    cursor: 'pointer',
                                                    border: '1px solid #cbd5e1',
                                                    position: 'relative',
                                                    flexShrink: 0
                                                }}
                                            >
                                                <img
                                                    src={receiptImgUrl}
                                                    alt="Comprobante"
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                />
                                                <div style={{
                                                    position: 'absolute',
                                                    bottom: 0,
                                                    left: 0,
                                                    right: 0,
                                                    background: 'rgba(0,0,0,0.6)',
                                                    color: '#fff',
                                                    fontSize: '0.7rem',
                                                    textAlign: 'center',
                                                    padding: '2px 0'
                                                }}>
                                                    <i className="bi bi-zoom-in"></i> Ver
                                                </div>
                                            </div>

                                            {/* Datos Extraídos */}
                                            <div style={{ flex: 1, fontSize: '0.85rem' }}>
                                                <div style={{ marginBottom: '0.5rem' }}>
                                                    <span style={{ color: '#64748b', display: 'block' }}>Monto Detectado (OCR):</span>
                                                    <strong style={{ fontSize: '1.25rem', color: receipt.extracted_amount ? '#15803d' : '#e11d48' }}>
                                                        {receipt.extracted_amount ? `$${receipt.extracted_amount} USD` : 'No detectado'}
                                                    </strong>
                                                </div>

                                                {ocr.bank && (
                                                    <div style={{ marginBottom: '0.35rem' }}>
                                                        <span style={{ color: '#64748b' }}>Banco:</span> <strong>{ocr.bank}</strong>
                                                    </div>
                                                )}

                                                {ocr.reference && (
                                                    <div style={{ marginBottom: '0.35rem' }}>
                                                        <span style={{ color: '#64748b' }}>Ref:</span> <code>{ocr.reference}</code>
                                                    </div>
                                                )}

                                                {receipt.rejection_reason && (
                                                    <div style={{ marginTop: '0.5rem', background: '#f8fafc', padding: '0.4rem', borderRadius: '4px', fontSize: '0.8rem', color: '#475569' }}>
                                                        {receipt.rejection_reason}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Botones de Acción */}
                                        <div style={{ padding: '0.75rem 1rem', background: '#f8fafc', borderTop: '1px solid #f1f5f9', display: 'flex', gap: '0.75rem' }}>
                                            <button
                                                onClick={() => handleApprove(receipt.id)}
                                                disabled={actionLoading}
                                                style={{
                                                    flex: 1,
                                                    background: '#16a34a',
                                                    color: '#fff',
                                                    border: 'none',
                                                    padding: '0.6rem',
                                                    borderRadius: '6px',
                                                    fontWeight: '600',
                                                    cursor: 'pointer',
                                                    fontSize: '0.85rem',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '0.4rem'
                                                }}
                                            >
                                                <i className="bi bi-check-circle-fill"></i> Aprobar
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setRejectingReceipt(receipt);
                                                    setRejectionReason('');
                                                }}
                                                disabled={actionLoading}
                                                style={{
                                                    flex: 1,
                                                    background: '#dc2626',
                                                    color: '#fff',
                                                    border: 'none',
                                                    padding: '0.6rem',
                                                    borderRadius: '6px',
                                                    fontWeight: '600',
                                                    cursor: 'pointer',
                                                    fontSize: '0.85rem',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '0.4rem'
                                                }}
                                            >
                                                <i className="bi bi-x-circle-fill"></i> Rechazar
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* TAB: Historial de Comprobantes */}
            {activeTab === 'history' && (
                <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '1.5rem' }}>
                    <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            style={{ padding: '0.6rem 1rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem' }}
                        >
                            <option value="ALL">Todos los Estados</option>
                            <option value="PENDING">Pendientes</option>
                            <option value="APPROVED">Aprobados</option>
                            <option value="REJECTED">Rechazados</option>
                        </select>
                    </div>

                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.9rem' }}>
                            <thead>
                                <tr style={{ borderBottom: '2px solid #e2e8f0', color: '#475569' }}>
                                    <th style={{ padding: '0.75rem' }}>Fecha</th>
                                    <th style={{ padding: '0.75rem' }}>Usuario / Local</th>
                                    <th style={{ padding: '0.75rem' }}>Ciclo</th>
                                    <th style={{ padding: '0.75rem' }}>Monto OCR</th>
                                    <th style={{ padding: '0.75rem' }}>Estado</th>
                                    <th style={{ padding: '0.75rem' }}>Auditado por</th>
                                    <th style={{ padding: '0.75rem' }}>Comprobante</th>
                                </tr>
                            </thead>
                            <tbody>
                                {receipts.map((r) => {
                                    const rImg = cobranzasService.getMediaUrl(r.image_url);
                                    return (
                                        <tr key={r.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                            <td style={{ padding: '0.75rem', color: '#64748b' }}>{new Date(r.uploaded_at).toLocaleDateString()}</td>
                                            <td style={{ padding: '0.75rem', fontWeight: '500' }}>
                                                {r.user_details?.first_name ? `${r.user_details.first_name} ${r.user_details.last_name}` : r.user_details?.email}
                                            </td>
                                            <td style={{ padding: '0.75rem' }}><code>{r.billing_cycle}</code></td>
                                            <td style={{ padding: '0.75rem', fontWeight: 'bold', color: r.extracted_amount ? '#15803d' : '#94a3b8' }}>
                                                {r.extracted_amount ? `$${r.extracted_amount}` : '-'}
                                            </td>
                                            <td style={{ padding: '0.75rem' }}>
                                                <span style={{
                                                    padding: '0.2rem 0.6rem',
                                                    borderRadius: '6px',
                                                    fontSize: '0.75rem',
                                                    fontWeight: 'bold',
                                                    background: r.status === 'APPROVED' ? '#dcfce7' : (r.status === 'REJECTED' ? '#fee2e2' : '#dbeafe'),
                                                    color: r.status === 'APPROVED' ? '#15803d' : (r.status === 'REJECTED' ? '#dc2626' : '#1d4ed8'),
                                                }}>
                                                    {r.status}
                                                </span>
                                            </td>
                                            <td style={{ padding: '0.75rem', color: '#64748b' }}>{r.reviewed_by_name || '-'}</td>
                                            <td style={{ padding: '0.75rem' }}>
                                                <button
                                                    onClick={() => setSelectedImage(rImg)}
                                                    style={{ background: 'transparent', border: 'none', color: '#0284c7', cursor: 'pointer', fontWeight: '600' }}
                                                >
                                                    <i className="bi bi-eye"></i> Ver Imagen
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* TAB: Directorio de Cuentas */}
            {activeTab === 'accounts' && (
                <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '1.5rem' }}>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.9rem' }}>
                            <thead>
                                <tr style={{ borderBottom: '2px solid #e2e8f0', color: '#475569' }}>
                                    <th style={{ padding: '0.75rem' }}>Local / Cuenta</th>
                                    <th style={{ padding: '0.75rem' }}>Usuario</th>
                                    <th style={{ padding: '0.75rem' }}>Mensualidad</th>
                                    <th style={{ padding: '0.75rem' }}>Día Límite / Corte</th>
                                    <th style={{ padding: '0.75rem' }}>Estado</th>
                                    <th style={{ padding: '0.75rem' }}>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {accounts.map((acc) => (
                                    <tr key={acc.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                        <td style={{ padding: '0.75rem', fontWeight: '600' }}>{acc.account_name || 'Sin nombre'}</td>
                                        <td style={{ padding: '0.75rem', color: '#64748b' }}>{acc.user_details?.email}</td>
                                        <td style={{ padding: '0.75rem', fontWeight: 'bold' }}>${acc.monthly_amount}</td>
                                        <td style={{ padding: '0.75rem' }}>Aviso: Día {acc.due_day} | Corte: Día {acc.cutoff_day}</td>
                                        <td style={{ padding: '0.75rem' }}>
                                            <span style={{
                                                padding: '0.25rem 0.75rem',
                                                borderRadius: '6px',
                                                fontSize: '0.8rem',
                                                fontWeight: 'bold',
                                                background: acc.status === 'ACTIVE' ? '#dcfce7' : (acc.status === 'BLOCKED' || acc.status === 'REJECTED' ? '#fee2e2' : '#fef9c3'),
                                                color: acc.status === 'ACTIVE' ? '#15803d' : (acc.status === 'BLOCKED' || acc.status === 'REJECTED' ? '#dc2626' : '#854d0e'),
                                            }}>
                                                {acc.status}
                                            </span>
                                        </td>
                                        <td style={{ padding: '0.75rem' }}>
                                            <button
                                                onClick={() => setEditingAccount(acc)}
                                                style={{
                                                    background: '#f1f5f9',
                                                    border: '1px solid #cbd5e1',
                                                    padding: '0.35rem 0.75rem',
                                                    borderRadius: '6px',
                                                    cursor: 'pointer',
                                                    fontWeight: '500',
                                                    fontSize: '0.8rem'
                                                }}
                                            >
                                                <i className="bi bi-pencil-square"></i> Editar
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Modal para Cortar Servicio Manualmente */}
            {showCutServiceModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0,0,0,0.6)',
                    zIndex: 3500,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '1rem'
                }}>
                    <div style={{ background: '#fff', borderRadius: '16px', maxWidth: '520px', width: '100%', padding: '1.75rem', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.2)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                            <div style={{ width: '42px', height: '42px', borderRadius: '50%', background: '#fee2e2', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem' }}>
                                <i className="bi bi-exclamation-triangle-fill"></i>
                            </div>
                            <div>
                                <h3 style={{ margin: 0, color: '#991b1b', fontWeight: 'bold', fontSize: '1.15rem' }}>
                                    Confirmar Corte de Servicio Manual
                                </h3>
                                <p style={{ margin: 0, fontSize: '0.825rem', color: '#64748b' }}>
                                    Esta acción bloqueará de inmediato el sistema a todos los usuarios.
                                </p>
                            </div>
                        </div>

                        <p style={{ fontSize: '0.9rem', color: '#334155', lineHeight: '1.5', marginBottom: '1rem' }}>
                            Al cortar el servicio, los empleados y administradores no podrán facturar ni operar en el POS.
                            Se les mostrará una pantalla bloqueada con tu imagen de pago y el formulario para subir el comprobante.
                        </p>

                        <div style={{ marginBottom: '1.5rem' }}>
                            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#334155', marginBottom: '0.35rem' }}>
                                Motivo de la Suspensión (Visible para los usuarios):
                            </label>
                            <textarea
                                rows={3}
                                value={cutServiceReason}
                                onChange={(e) => setCutServiceReason(e.target.value)}
                                placeholder="Ej: Servicio suspendido administrativamente por falta de pago de suscripción mensual."
                                style={{ width: '100%', padding: '0.65rem 0.85rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.875rem' }}
                            />
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                            <button
                                type="button"
                                onClick={() => setShowCutServiceModal(false)}
                                style={{ background: '#f1f5f9', border: '1px solid #cbd5e1', padding: '0.65rem 1.25rem', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                disabled={actionLoading}
                                onClick={() => handleToggleManualBlock(true, cutServiceReason)}
                                style={{
                                    background: '#dc2626',
                                    color: '#ffffff',
                                    border: 'none',
                                    padding: '0.65rem 1.5rem',
                                    borderRadius: '8px',
                                    fontWeight: 'bold',
                                    cursor: actionLoading ? 'not-allowed' : 'pointer'
                                }}
                            >
                                {actionLoading ? 'Cortando Servicio...' : 'Sí, Cortar Servicio Ahora'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Zoom Imagen */}
            {selectedImage && (
                <div
                    onClick={() => setSelectedImage(null)}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0,0,0,0.85)',
                        zIndex: 4000,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '2rem'
                    }}
                >
                    <div style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }}>
                        <img
                            src={selectedImage}
                            alt="Comprobante ampliado"
                            style={{ maxWidth: '100%', maxHeight: '85vh', borderRadius: '8px', objectFit: 'contain' }}
                        />
                        <button
                            onClick={() => setSelectedImage(null)}
                            style={{
                                position: 'absolute',
                                top: '-40px',
                                right: '0',
                                background: 'transparent',
                                border: 'none',
                                color: '#fff',
                                fontSize: '2rem',
                                cursor: 'pointer'
                            }}
                        >
                            &times;
                        </button>
                    </div>
                </div>
            )}

            {/* Modal Motivo Rechazo */}
            {rejectingReceipt && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0,0,0,0.5)',
                    zIndex: 3500,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '1rem'
                }}>
                    <div style={{ background: '#fff', borderRadius: '12px', maxWidth: '480px', width: '100%', padding: '1.5rem', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.2)' }}>
                        <h3 style={{ margin: '0 0 1rem 0', color: '#dc2626', fontWeight: 'bold' }}>
                            <i className="bi bi-x-circle-fill"></i> Rechazar Comprobante de Pago
                        </h3>
                        <p style={{ fontSize: '0.9rem', color: '#475569', marginBottom: '1rem' }}>
                            Al rechazar este comprobante, el sistema del usuario se <strong>bloqueará inmediatamente</strong> y se le mostrará el motivo indicado.
                        </p>
                        <form onSubmit={handleRejectSubmit}>
                            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#334155', marginBottom: '0.5rem' }}>
                                Motivo del Rechazo:
                            </label>
                            <textarea
                                required
                                rows={3}
                                value={rejectionReason}
                                onChange={(e) => setRejectionReason(e.target.value)}
                                placeholder="Ej: El monto transferido no coincide con el valor de la mensualidad / Comprobante ilegible..."
                                style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem', marginBottom: '1.25rem' }}
                            />
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                                <button
                                    type="button"
                                    onClick={() => setRejectingReceipt(null)}
                                    style={{ background: '#f1f5f9', border: '1px solid #cbd5e1', padding: '0.6rem 1rem', borderRadius: '6px', cursor: 'pointer', fontWeight: '500' }}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={actionLoading}
                                    style={{ background: '#dc2626', color: '#fff', border: 'none', padding: '0.6rem 1.25rem', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}
                                >
                                    Confirmar Rechazo y Bloquear
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal Editar Cuenta */}
            {editingAccount && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0,0,0,0.5)',
                    zIndex: 3500,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '1rem'
                }}>
                    <div style={{ background: '#fff', borderRadius: '12px', maxWidth: '500px', width: '100%', padding: '1.5rem' }}>
                        <h3 style={{ margin: '0 0 1.25rem 0', fontWeight: 'bold', color: '#1e293b' }}>
                            Editar Parámetros de Cobro
                        </h3>
                        <form onSubmit={handleSaveAccount}>
                            <div style={{ marginBottom: '1rem' }}>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.35rem' }}>Nombre del Local / Cuenta:</label>
                                <input
                                    type="text"
                                    value={editingAccount.account_name}
                                    onChange={(e) => setEditingAccount({ ...editingAccount, account_name: e.target.value })}
                                    style={{ width: '100%', padding: '0.65rem', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                                />
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.35rem' }}>Mensualidad ($):</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={editingAccount.monthly_amount}
                                        onChange={(e) => setEditingAccount({ ...editingAccount, monthly_amount: e.target.value })}
                                        style={{ width: '100%', padding: '0.65rem', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.35rem' }}>Estado Forzado:</label>
                                    <select
                                        value={editingAccount.status}
                                        onChange={(e) => setEditingAccount({ ...editingAccount, status: e.target.value })}
                                        style={{ width: '100%', padding: '0.65rem', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                                    >
                                        <option value="ACTIVE">ACTIVE (Al Día)</option>
                                        <option value="PENDING_PAYMENT">PENDING_PAYMENT (Aviso)</option>
                                        <option value="UNDER_REVIEW">UNDER_REVIEW (En Revisión)</option>
                                        <option value="BLOCKED">BLOCKED (Bloqueado)</option>
                                        <option value="REJECTED">REJECTED (Rechazado)</option>
                                        <option value="EXEMPT">EXEMPT (Exento)</option>
                                    </select>
                                </div>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.25rem' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.35rem' }}>Día Límite Aviso:</label>
                                    <input
                                        type="number"
                                        min="1"
                                        max="31"
                                        value={editingAccount.due_day}
                                        onChange={(e) => setEditingAccount({ ...editingAccount, due_day: parseInt(e.target.value) || 5 })}
                                        style={{ width: '100%', padding: '0.65rem', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.35rem' }}>Día de Corte / Bloqueo:</label>
                                    <input
                                        type="number"
                                        min="1"
                                        max="31"
                                        value={editingAccount.cutoff_day}
                                        onChange={(e) => setEditingAccount({ ...editingAccount, cutoff_day: parseInt(e.target.value) || 6 })}
                                        style={{ width: '100%', padding: '0.65rem', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                                    />
                                </div>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                                <button
                                    type="button"
                                    onClick={() => setEditingAccount(null)}
                                    style={{ background: '#f1f5f9', border: '1px solid #cbd5e1', padding: '0.6rem 1rem', borderRadius: '6px', cursor: 'pointer' }}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={actionLoading}
                                    style={{ background: 'var(--primary-color, #4a1f0c)', color: '#fff', border: 'none', padding: '0.6rem 1.25rem', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}
                                >
                                    Guardar Cambios
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PanelCobranzas;
